import React, { useState } from "react";
import ReusableTable from "../../../Reuseable/ReusableTable/ReusableTable";
import { Button, Flex, Space, Skeleton } from "antd";
import RulesEditModal from "../RulesEditModal/RulesEditModal";
import DeleteUserModal from "../../AgencyComp/UserComp/DeleteUserModal";
import { ReactComponent as EditIcon } from "../../../assets/Icons/Editicon.svg";
import { ReactComponent as TrashIcon } from "../../../assets/Icons/TrashIcon.svg";

import "./RulesTable.scss";
import { deleteRules } from "../../../assets/api/othres";
import { useDispatch, useSelector } from "react-redux";
import { fetchRulesList } from "../../../store/rulesSlice";
import toast from "react-hot-toast";

const RulesTable = ({
  rules_list,
  bankListOption,
  productListOption,
  areaCodeListOption,
  bucketOptions,
  languageOptions,
  loading,
  handlePaginationChange,
}) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState({
    open: false,
    id: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [editableRule, setEditableRule] = useState(null);
  const { token } = useSelector((state) => state.auth);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const dispatch = useDispatch();

  const dataSource = rules_list?.results?.map((item, index) => ({
    key: item.id,
    name: item.user?.name || "N/A",
    email: item.user?.email || "N/A",
    user_role: item.user?.role || "N/A",
    bucket: item.bucket || "N/A",
    area: item?.area || "NA",
    product: item?.product,
    portfolio: item?.portfolio,
    language: item?.language,
    region: item?.region,
  }));

  /* Each field is a label plus zero or more values. Multi-value fields were
     rendered as a comma-joined string, which read as one long sentence; they
     are chips now so each value is legible on its own. */
  const ruleDetails = (record) => [
    {
      label: "Area Codes",
      values: (record.area || []).map((area) => area.name).filter(Boolean),
    },
    {
      label: "Products",
      values: (record.product || []).map((prod) => prod.name).filter(Boolean),
    },
    {
      label: "Banks",
      values: (record.portfolio || []).map((bank) => bank.name).filter(Boolean),
    },
    {
      label: "Region",
      values: record.region?.name ? [record.region.name] : [],
    },
    {
      label: "Language",
      values: record.language ? [record.language] : [],
    },
  ];

  const myExpandedRowRender = (record) => (
    <div className="Expanded-main">
      {ruleDetails(record).map(({ label, values }) => (
        <div className="detail-container" key={label}>
          <span className="detail-label">{label}</span>

          {values.length > 0 ? (
            <span className="detail-values">
              {values.map((value) => (
                <span className="detail-chip" key={value}>
                  {value}
                </span>
              ))}
            </span>
          ) : (
            <span className="detail-empty">Not set</span>
          )}
        </div>
      ))}
    </div>
  );

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 226,
      render: (name) =>
        loading ? <Skeleton.Input size="small" active /> : name || "-",
    },
    {
      title: "Aging Bucket",
      dataIndex: "bucket",
      key: "bucket",
      width: 210,
      render: (bucket) =>
        loading ? <Skeleton.Input size="small" active /> : bucket || "-",
    },
    {
      title: "User Role",
      dataIndex: "user_role",
      key: "user_role",
      width: 195,
      render: (role) =>
        loading ? <Skeleton.Input size="small" active /> : role || "-",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 330,
      render: (email) =>
        loading ? <Skeleton.Input size="small" active /> : email || "-",
    },
    {
      title: "Actions",
      key: "actions",
      width: 89,
      render: (_, record) =>
        loading ? (
          <Skeleton.Input size="small" active />
        ) : (
          <Space>
            <Button
              type="text"
              icon={<EditIcon />}
              onClick={() => {
                setIsEditModalOpen(true);
                setEditableRule(record);
              }}
            />

          </Space>
        ),
    },
  ];

  const handleDeleteRules = async () => {
    try {
      setLoadingDelete(true);
      const res = await deleteRules(isDeleteModalOpen.id, token);
      if (res?.status === 200) {
        setIsDeleteModalOpen({ open: false, id: null });
        dispatch(
          fetchRulesList({ page: currentPage, page_size: pageSize }),
        );
        setCurrentPage(1);
        setPageSize(10);
        toast.success("Rules deleted successfully!");
        setLoadingDelete(false);
      } else {
        toast.error("Failed to delete rules.");
        setLoadingDelete(false);
      }
    } catch (error) {
      setLoadingDelete(false);
    }
  };

  return (
    <div>
      <ReusableTable
        columns={columns}
        dataSource={loading ? Array(5).fill({}) : dataSource}
        rowKey="key"
        bordered
        pageSize={pageSize}
        CurrentPageNo={currentPage}
        totalItems={rules_list?.count || 0}
        setPageSize={setPageSize}
        triggerChange={(page, size) => {
          setCurrentPage(page);
          setPageSize(size);
          handlePaginationChange(page, size);
        }}
        expandedRowRender={!loading ? myExpandedRowRender : undefined}
      />

      <DeleteUserModal
        open={isDeleteModalOpen?.open}
        onClose={() =>
          setIsDeleteModalOpen((prev) => ({
            ...prev,
            open: false,
          }))
        }
        onDelete={handleDeleteRules}
        loading={loadingDelete}
      />
      <RulesEditModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        editableRule={editableRule}
        bankListOption={bankListOption}
        productListOption={productListOption}
        areaCodeListOption={areaCodeListOption}
        bucketOptions={bucketOptions}
        languageOptions={languageOptions}
      />
    </div>
  );
};

export default RulesTable;
